import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../utils/constants';

const TermsOfService = () => {
  const sectionStyle = {
    marginBottom: '2rem',
  };

  const headingStyle = {
    fontFamily: '"Playfair Display", serif',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1A1A1A',
    marginBottom: '1rem',
  };

  const paragraphStyle = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#444444',
    marginBottom: '0.75rem',
  };

  const listStyle = {
    fontFamily: '"DM Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#444444',
    paddingLeft: '1.5rem',
    marginBottom: '0.75rem',
  };

  return (
    <>
      <Helmet>
        <title>Terms of Service | {COMPANY_INFO.name}</title>
        <meta name="description" content={`Terms of Service for ${COMPANY_INFO.name}. Read our terms and conditions for using our services and applications.`} />
      </Helmet>

      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '8rem 1.5rem 4rem',
      }}>
        <h1 style={{
          fontFamily: '"Playfair Display", serif',
          fontSize: 'clamp(2rem, 5vw, 3rem)',
          fontWeight: 700,
          color: '#1A1A1A',
          marginBottom: '0.5rem',
        }}>
          Terms of Service
        </h1>
        <p style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '0.9rem',
          color: '#888888',
          marginBottom: '3rem',
        }}>
          Last updated: October 2026
        </p>

        <div style={sectionStyle}>
          <p style={paragraphStyle}>
            Welcome to {COMPANY_INFO.name}. These Terms of Service ("Terms") govern your use of the website {COMPANY_INFO.website}, our mobile applications, software products, and related services (collectively, the "Services") operated by {COMPANY_INFO.fullName} ("we", "our", or "us").
          </p>
          <p style={paragraphStyle}>
            By accessing or using our Services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use our Services.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. About Us</h2>
          <p style={paragraphStyle}>
            {COMPANY_INFO.fullName} is a technology company that builds apps, software, and automation solutions for businesses. Our services include but are not limited to WhatsApp Business automation, AI-powered customer support systems, automated lead generation platforms, end-to-end process automation, custom mobile and web application development, and software consulting.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Use of Services</h2>
          <p style={paragraphStyle}>You agree to use our Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul style={listStyle}>
            <li>Use the Services in any way that violates any applicable law or regulation</li>
            <li>Attempt to gain unauthorised access to any part of our Services or systems</li>
            <li>Interfere with or disrupt the integrity or performance of the Services</li>
            <li>Use the Services to transmit harmful, offensive, or illegal content</li>
            <li>Reverse engineer, decompile, or disassemble any part of our software</li>
            <li>Use automated systems or bots to access the Services without our written permission</li>
            <li>Resell, redistribute, or sublicense any part of our Services without authorisation</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Accounts and Registration</h2>
          <p style={paragraphStyle}>
            Some of our Services may require you to create an account. When you do, you agree to provide accurate and complete information and to keep your account credentials secure. You are responsible for all activity that occurs under your account.
          </p>
          <p style={paragraphStyle}>
            We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent or abusive behaviour.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Intellectual Property</h2>
          <p style={paragraphStyle}>
            All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, software, source code, designs, and trademarks, are the exclusive property of {COMPANY_INFO.fullName} and are protected by intellectual property laws.
          </p>
          <p style={paragraphStyle}>
            You may not copy, modify, distribute, sell, or lease any part of our Services or included software, nor may you reverse engineer or attempt to extract the source code, unless applicable laws prohibit these restrictions or you have our written permission.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Software and Applications</h2>
          <p style={paragraphStyle}>
            We develop and publish mobile applications and software products. By downloading or using any of our applications:
          </p>
          <ul style={listStyle}>
            <li>You are granted a limited, non-exclusive, non-transferable licence to use the application for personal or business purposes</li>
            <li>You agree not to modify, adapt, or create derivative works from our applications</li>
            <li>You acknowledge that application updates may be required and agree to install them for continued use</li>
            <li>You understand that certain features may require an internet connection and may incur data charges from your network provider</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Payment and Billing</h2>
          <p style={paragraphStyle}>
            Certain Services may require payment. By purchasing a service, you agree to pay all fees and charges associated with your selected plan. All fees are quoted in Indian Rupees (INR) unless otherwise stated.
          </p>
          <ul style={listStyle}>
            <li>One-time setup fees are due upon commencement of work</li>
            <li>Monthly or recurring fees are billed in advance on a monthly basis</li>
            <li>All payments are non-refundable unless otherwise specified in a separate agreement</li>
            <li>We reserve the right to change pricing with 30 days' notice</li>
          </ul>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Service Availability</h2>
          <p style={paragraphStyle}>
            We strive to ensure our Services are available at all times. However, we do not guarantee uninterrupted or error-free operation. We may temporarily suspend access for maintenance, updates, or reasons beyond our control.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Content and Data</h2>
          <p style={paragraphStyle}>
            You retain ownership of any content or data you provide through our Services. By submitting content, you grant us a non-exclusive, worldwide licence to use, process, and store such content solely for the purpose of providing and improving our Services.
          </p>
          <p style={paragraphStyle}>
            We are not responsible for any loss of data. You are encouraged to maintain your own backups of important information.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Third-Party Services</h2>
          <p style={paragraphStyle}>
            Our Services may integrate with or contain links to third-party services (e.g., WhatsApp, Google, Firebase, payment gateways). We are not responsible for the content, privacy policies, or practices of any third-party services. Your use of such services is governed by their respective terms and conditions.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Limitation of Liability</h2>
          <p style={paragraphStyle}>
            To the fullest extent permitted by law, {COMPANY_INFO.fullName} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising out of or related to your use of our Services.
          </p>
          <p style={paragraphStyle}>
            Our total liability for any claim arising from these Terms or our Services shall not exceed the amount you paid to us in the twelve (12) months preceding the claim.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Indemnification</h2>
          <p style={paragraphStyle}>
            You agree to indemnify and hold harmless {COMPANY_INFO.fullName}, its directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising out of your use of the Services, your violation of these Terms, or your infringement of any third-party rights.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Termination</h2>
          <p style={paragraphStyle}>
            We may terminate or suspend your access to our Services immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Services ceases immediately.
          </p>
          <p style={paragraphStyle}>
            You may terminate your use of the Services at any time by discontinuing use and, if applicable, closing your account.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Governing Law</h2>
          <p style={paragraphStyle}>
            These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts in India.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>14. Changes to These Terms</h2>
          <p style={paragraphStyle}>
            We reserve the right to modify these Terms at any time. We will notify you of changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Services after changes are posted constitutes your acceptance of the revised Terms.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>15. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have any questions about these Terms, please contact us:
          </p>
          <ul style={listStyle}>
            <li><strong>Company:</strong> {COMPANY_INFO.fullName}</li>
            <li><strong>Email:</strong> <a href={`mailto:${COMPANY_INFO.email}`} style={{ color: '#1A1A1A', fontWeight: 600 }}>{COMPANY_INFO.email}</a></li>
            <li><strong>Website:</strong> {COMPANY_INFO.website}</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
